import * as React from 'react';

import { cn } from '@/lib/utils';

import { ColorPicker } from './ColorPicker';
import { NameAutocomplete } from './NameAutocomplete';

interface AutocompleteState {
  visible: boolean;
  query: string;
  startPos: number;
  selectedIndex: number;
}

interface RichTextInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  className?: string;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  ghostText?: string;
  autocompleteItems?: Array<{ name: string; thumbnail_url?: string | null }>;
  /** Defaults true for legacy command surfaces; narrative composer opts into Cmd/Ctrl+Enter. */
  submitOnEnter?: boolean;
  /** Show the bold/italic/strike/link/colour controls (default). Off for a plain command line (#3857). */
  formatting?: boolean;
  /** Class for the textarea itself, e.g. a monospace face for a command line (#3857). */
  textareaClassName?: string;
  disabled?: boolean;
  /** Block delivery while allowing the player to keep writing. */
  submitDisabled?: boolean;
}

// M10: Multi-word names (e.g. "Crucible Mundi") can't be typed manually
// because spaces terminate the scan. Users should rely on the autocomplete
// dropdown, which inserts the full name regardless of spaces.
function detectMention(
  text: string,
  cursorPos: number
): { startPos: number; query: string } | null {
  let i = cursorPos - 1;
  while (i >= 0 && text[i] !== '@' && text[i] !== ' ' && text[i] !== '\n') {
    i--;
  }
  if (
    i >= 0 &&
    text[i] === '@' &&
    (i === 0 || text[i - 1] === ' ' || text[i - 1] === '\n' || text[i - 1] === ',')
  ) {
    const query = text.substring(i + 1, cursorPos);
    return { startPos: i, query };
  }
  return null;
}

function wrapSelection(
  textarea: HTMLTextAreaElement,
  before: string,
  after: string,
  _value: string,
  onChange: (value: string) => void
) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  // Read from DOM to avoid stale closure values during rapid typing
  const currentValue = textarea.value;
  const selected = currentValue.slice(start, end);

  const newValue =
    currentValue.slice(0, start) + before + selected + after + currentValue.slice(end);
  onChange(newValue);

  // Position cursor after the wrapping operation
  requestAnimationFrame(() => {
    textarea.focus();
    if (selected.length > 0) {
      // Select the wrapped text (after the before marker)
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = start + before.length + selected.length;
    } else {
      // Place cursor between markers
      const cursorPos = start + before.length;
      textarea.selectionStart = cursorPos;
      textarea.selectionEnd = cursorPos;
    }
  });
}

export function RichTextInput({
  value,
  onChange,
  onSubmit,
  onKeyDown,
  rows = 3,
  className,
  leftSlot,
  rightSlot,
  ghostText,
  autocompleteItems,
  submitOnEnter = true,
  formatting = true,
  textareaClassName,
  disabled = false,
  submitDisabled = false,
}: RichTextInputProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [autocompleteState, setAutocompleteState] = React.useState<AutocompleteState | null>(null);

  // Keep the five-line starting surface, then grow only within the viewport.
  // Beyond 35% of the viewport the editor scrolls internally instead of
  // pushing the reader and Here panel off-screen.
  React.useLayoutEffect(() => {
    const resize = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const maxHeight = Math.floor(viewportHeight * 0.35);
      textarea.style.height = 'auto';
      const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${nextHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    };
    resize();
    window.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('resize', resize);
    };
  }, [value]);

  const filteredItems = React.useMemo(() => {
    if (!autocompleteState?.visible || !autocompleteItems) return [];
    return autocompleteItems.filter((c) =>
      c.name.toLowerCase().startsWith(autocompleteState.query.toLowerCase())
    );
  }, [autocompleteState?.visible, autocompleteState?.query, autocompleteItems]);

  const handleWrap = React.useCallback(
    (before: string, after: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      wrapSelection(textarea, before, after, value, onChange);
    },
    [value, onChange]
  );

  const handleAutocompleteSelect = React.useCallback(
    (name: string) => {
      if (!autocompleteState) return;
      const before = value.slice(0, autocompleteState.startPos);
      const after = value.slice(autocompleteState.startPos + 1 + autocompleteState.query.length);
      const newValue = before + '@' + name + after;
      onChange(newValue);
      setAutocompleteState(null);

      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          const cursorPos = autocompleteState.startPos + 1 + name.length;
          textarea.focus();
          textarea.selectionStart = cursorPos;
          textarea.selectionEnd = cursorPos;
        }
      });
    },
    [autocompleteState, value, onChange]
  );

  const handleLinkInsert = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const selected = currentValue.slice(start, end);

    let insert: string;
    let cursorStart: number;
    let cursorEnd: number;

    if (selected.length > 0) {
      // Wrap selection as label; select the URL placeholder so user can type it
      insert = `[${selected}](https://)`;
      cursorStart = start + 1 + selected.length + 2; // position of 'h' in 'https://'
      cursorEnd = cursorStart + 'https://'.length;
    } else {
      // No selection: insert template with cursor inside [] so user types label first
      insert = '[](https://)';
      cursorStart = start + 1;
      cursorEnd = start + 1;
    }

    const newValue = currentValue.slice(0, start) + insert + currentValue.slice(end);
    onChange(newValue);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = cursorStart;
      textarea.selectionEnd = cursorEnd;
    });
  }, [onChange]);

  /**
   * Drive the open autocomplete popup. Returns true when the key was consumed,
   * so the caller knows not to treat it as text or as a formatting shortcut.
   */
  const handleAutocompleteKey = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!autocompleteState?.visible || filteredItems.length === 0) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAutocompleteState((prev) =>
          prev
            ? { ...prev, selectedIndex: Math.min(prev.selectedIndex + 1, filteredItems.length - 1) }
            : null
        );
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAutocompleteState((prev) =>
          prev ? { ...prev, selectedIndex: Math.max(prev.selectedIndex - 1, 0) } : null
        );
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        // I6: the filtered list can shrink as the user types, leaving
        // selectedIndex pointing past the end — clamp before indexing.
        const selected =
          filteredItems[Math.min(autocompleteState.selectedIndex, filteredItems.length - 1)];
        if (selected) {
          handleAutocompleteSelect(selected.name);
        }
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setAutocompleteState(null);
        return true;
      }
      return false;
    },
    [autocompleteState, filteredItems, handleAutocompleteSelect]
  );

  /** Ctrl/Cmd formatting shortcuts. Returns true when the key was consumed. */
  const handleFormattingKey = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!e.ctrlKey && !e.metaKey) return false;

      const wrap = (open: string, close: string) => {
        e.preventDefault();
        handleWrap(open, close);
        return true;
      };
      if (e.key === 'b') return wrap('**', '**');
      if (e.key === 'i') return wrap('*', '*');
      if (e.shiftKey && (e.key === 'S' || e.key === 's')) return wrap('~~', '~~');
      if (e.key === 'k') {
        e.preventDefault();
        handleLinkInsert();
        return true;
      }
      return false;
    },
    [handleWrap, handleLinkInsert]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // The popup gets first refusal on every key, then formatting shortcuts.
      if (handleAutocompleteKey(e)) return;
      if (handleFormattingKey(e)) return;

      // Enter sends and Shift+Enter breaks the line — the chat-RP convention,
      // and since #3818 what the narrative composer uses too. A caller that
      // wants Enter for paragraphs passes submitOnEnter={false} and sends on
      // Cmd/Ctrl+Enter instead.
      if (
        e.key === 'Enter' &&
        !e.nativeEvent.isComposing &&
        (submitOnEnter ? !e.shiftKey : e.ctrlKey || e.metaKey)
      ) {
        e.preventDefault();
        if (!disabled && !submitDisabled) onSubmit();
        return;
      }

      onKeyDown?.(e);
    },
    [
      handleAutocompleteKey,
      handleFormattingKey,
      onSubmit,
      onKeyDown,
      submitOnEnter,
      disabled,
      submitDisabled,
    ]
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      if (autocompleteItems && autocompleteItems.length > 0) {
        const cursorPos = e.target.selectionStart;
        const mention = detectMention(newValue, cursorPos);
        if (mention) {
          setAutocompleteState({
            visible: true,
            query: mention.query,
            startPos: mention.startPos,
            selectedIndex: 0,
          });
        } else {
          setAutocompleteState(null);
        }
      }
    },
    [onChange, autocompleteItems]
  );

  const handleColorSelect = React.useCallback(
    (xtermIndex: number) => {
      handleWrap(`|[${xtermIndex}]`, '|n');
    },
    [handleWrap]
  );

  return (
    <div className={cn('overflow-hidden rounded-md border border-input shadow-sm', className)}>
      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center gap-0.5 border-b border-input bg-muted/50 px-1.5 py-1"
        role="toolbar"
        aria-label="Formatting toolbar"
      >
        {leftSlot}
        {formatting && (
          <>
            <button
              type="button"
              title="Bold (Ctrl+B)"
              aria-label="Bold"
              className="flex h-6 w-6 items-center justify-center rounded text-xs font-bold hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleWrap('**', '**')}
            >
              B
            </button>
            <button
              type="button"
              title="Italic (Ctrl+I)"
              aria-label="Italic"
              className="flex h-6 w-6 items-center justify-center rounded text-xs italic hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleWrap('*', '*')}
            >
              I
            </button>
            <button
              type="button"
              title="Strikethrough (Ctrl+Shift+S)"
              aria-label="Strikethrough"
              className="flex h-6 w-6 items-center justify-center rounded text-xs line-through hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleWrap('~~', '~~')}
            >
              S
            </button>
            <button
              type="button"
              title="Link (Ctrl+K)"
              aria-label="Link"
              className="flex h-6 w-6 items-center justify-center rounded text-xs hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleLinkInsert}
            >
              🔗
            </button>
            <div className="mx-1 h-4 w-px bg-border" />
            <ColorPicker onSelectColor={handleColorSelect} />
          </>
        )}
        {rightSlot}
        <button
          type="button"
          className="ml-auto min-h-11 min-w-11 shrink-0 rounded bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          onClick={onSubmit}
          aria-label="Send"
          disabled={disabled || submitDisabled}
        >
          Send
        </button>
      </div>

      {/* Textarea with ghost text and autocomplete */}
      <div className="relative">
        {!value && ghostText && (
          <div className="pointer-events-none absolute inset-0 px-3 py-2 text-sm text-muted-foreground/40">
            {ghostText}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={rows}
          spellCheck={true}
          disabled={disabled}
          className={cn(
            'relative max-h-[35dvh] w-full resize-none overflow-y-auto bg-transparent px-3 py-2 text-base focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            textareaClassName
          )}
        />
        {autocompleteItems && (
          <NameAutocomplete
            characters={filteredItems}
            query={autocompleteState?.query ?? ''}
            visible={autocompleteState?.visible ?? false}
            onSelect={handleAutocompleteSelect}
            onDismiss={() => setAutocompleteState(null)}
            selectedIndex={autocompleteState?.selectedIndex ?? 0}
          />
        )}
      </div>
    </div>
  );
}
